/* eslint-disable no-mixed-operators */
import React from "react";
//Material imports
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Person from "@mui/icons-material/Person";
import ClaimedIcon from "@mui/icons-material/LockOutlined";
import AttentionRequiredIcon from "@mui/icons-material/InfoOutlined";
import CheckBox from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
//Limitless imports
import { formatPercentage } from "../TableCellDataFormatters";
import { log as pilog } from "@limitless/common/utils";
import { formatPoints, formatCurrency, ellipsisText } from "../..";
import { RatingSystem } from "../../../customer/components";
import HoverDetails from "../../components/hover-details/HoverDetails";
import MessageState from "../../components/message-state/MessageState";
import TransactionState from "../../components/transaction-state/TransactionState";
import MessageNotesDialog from "../../components/dialogs/message-notes-dialog/MessageNotesDialog";
import DateLabel from "../../components/date-label/DateLabel";
import TextButton from "../../components/buttons/text-button/TextButton";
import TimeLeftCell from "../../components/cells/time-left-cell/TimeLeftCell";
import HoverCopyDetails from "../../components/hover-copy-details/HoverCopyDetails";
const iconStyles = {
  display: "flex",
  flex: 1,
  justifyContent: "center"
};
export const timeUnit = {
  SECONDS: "seconds",
  MINUTES: "minutes",
  HOURS: "hours",
  DAYS: "days"
};
export const getFormattedTime = (value, inputUnits) => {
  const valueInSeconds = () => {
    if (inputUnits === timeUnit.MINUTES) {
      return value * 60;
    }
    if (inputUnits === timeUnit.HOURS) {
      return value * 3600;
    }
    if (inputUnits === timeUnit.DAYS) {
      return value * 86400;
    }
    return value;
  };
  let seconds = parseInt(valueInSeconds(), 10);
  const days = Math.floor(seconds / (3600 * 24));
  seconds -= days * 3600 * 24;
  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  const timeString = {
    days: days ? `${days} day${days > 1 ? "s" : ""}` : "",
    hours: hours ? `${hours} hour${hours > 1 ? "s" : ""}` : "",
    minutes: minutes ? `${minutes} minute${minutes > 1 ? "s" : ""}` : "",
    seconds: seconds ? `${seconds} second${seconds > 1 ? "s" : ""}` : ""
  };
  const ts = [];
  Object.keys(timeString).forEach(interval => {
    if (timeString[interval] === "") {
      return;
    }
    ts.push(timeString[interval]);
  });
  return ts.join(", ");
};
export const renderSecsToSecsMinutesHours = (value, zeroOrNullResponse, inputUnits = timeUnit.SECONDS) => {
  if (value === null || value === 0) {
    return /*#__PURE__*/React.createElement(Typography, {
      color: "inherit",
      variant: "body1"
    }, zeroOrNullResponse);
  }
  const formattedTime = getFormattedTime(value, inputUnits);
  return /*#__PURE__*/React.createElement(Typography, {
    color: "inherit",
    variant: "body1"
  }, formattedTime);
};
export const renderDefaultCell = (value, rowData, onUpdate, shouldEllipse = false, align = "left", sx = {}) => {
  const textEllipsis = shouldEllipse ? {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "inherit"
  } : {};
  return /*#__PURE__*/React.createElement(Typography, {
    style: textEllipsis,
    color: "inherit",
    variant: "body1",
    align: align,
    sx: sx
  }, value);
};
export const renderPointsCell = value => {
  const val = !value || value === "" ? 0 : value;
  return /*#__PURE__*/React.createElement(Typography, {
    color: "inherit",
    variant: "body1",
    align: "center"
  }, formatPoints(val));
};
export const renderCurrencyCell = (value, formatOptions) => {
  const val = !value || value === "" ? 0 : value;
  const formatOpts = !formatOptions ? {} : formatOptions;
  return /*#__PURE__*/React.createElement(Typography, {
    color: "inherit",
    variant: "body1",
    align: "center"
  }, formatCurrency(val, formatOpts));
};
export const renderRatingCell = value => {
  return /*#__PURE__*/React.createElement(Typography, {
    color: "inherit",
    variant: "body1",
    align: "center"
  }, formatPercentage(value));
};
export const renderDateCell = (value, includeSeconds, showDateOnly, showDistanceInWords) => {
  return /*#__PURE__*/React.createElement(DateLabel, {
    value: value,
    includeSeconds: includeSeconds,
    showDateOnly: showDateOnly,
    showDistanceInWords: showDistanceInWords
  });
};
export const renderMillsecondDateCell = value => {
  return /*#__PURE__*/React.createElement(DateLabel, {
    value: value,
    customFormat: "DD/MM/YYYY HH:mm:ss:SSS"
  });
};
export const renderCustomerScoreCell = (value, message, showHover = true) => {
  if (!value || isNaN(value)) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  const score = /*#__PURE__*/React.createElement(RatingSystem, {
    message: message,
    disabled: true,
    useDefaultScore: false,
    compact: true
  });
  if (message && message.custTestimonial && showHover) {
    return /*#__PURE__*/React.createElement(HoverDetails, {
      renderItem: () => {
        return /*#__PURE__*/React.createElement(React.Fragment, null, score);
      },
      title: "Testimonial",
      value: message.custTestimonial
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, score);
};
export const renderBooleanCell = (value, onChange) => {
  let checkedValue = false;
  if (typeof value === "boolean") {
    checkedValue = value;
  } else if (!isNaN(value)) {
    checkedValue = Number.parseInt(value) === 1 ? true : false;
  } else if (typeof value === "string") {
    checkedValue = value === "true" ? true : false;
  }
  const readOnly = typeof onChange !== "function";
  if (!readOnly) {
    return /*#__PURE__*/React.createElement(Checkbox, {
      color: "secondary",
      onChange: () => onChange(!checkedValue),
      checked: checkedValue
    });
  }
  if (checkedValue) {
    return /*#__PURE__*/React.createElement(CheckBox, {
      color: "secondary"
    });
  } else {
    return /*#__PURE__*/React.createElement(CheckBoxOutlineBlankIcon, {
      color: "secondary"
    });
  }
};
export const renderAvatarCell = value => {
  if (value !== "") {
    return /*#__PURE__*/React.createElement(Avatar, {
      src: `data:image/png;base64, ${value}`
    });
  } else {
    return /*#__PURE__*/React.createElement(Avatar, null, /*#__PURE__*/React.createElement(Person, null));
  }
};
export const renderImageCell = (value, tableMeta, imageStyle) => {
  return /*#__PURE__*/React.createElement("img", {
    src: value,
    style: imageStyle
  });
};
export const renderSubMetricCell = (value, codeDescriptions) => {
  const log = pilog.getLogger("TableCellRenderers renderSubMetricCell");
  let valueToDisplay = value;
  let metric = "";
  let tooltip = null;
  try {
    const submetricSplit = value.split(" - ");
    metric = submetricSplit[0];
    valueToDisplay = submetricSplit[1];
    if (submetricSplit.length !== 1 && codeDescriptions[metric]) {
      tooltip = valueToDisplay;
      valueToDisplay = codeDescriptions[metric][valueToDisplay] !== "none" ? codeDescriptions[metric][valueToDisplay] : valueToDisplay;
    }
  } catch (error) {
    log.info("Unable to parse sub metric ", value);
    log.info(error);
  }
  if (tooltip) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      title: tooltip
    }, /*#__PURE__*/React.createElement(Typography, {
      color: "inherit",
      variant: "body1"
    }, valueToDisplay));
  }
  return /*#__PURE__*/React.createElement(Typography, {
    color: "inherit",
    variant: "body1"
  }, valueToDisplay);
};
export const renderDateInWordsCell = (value, showWarningColors) => {
  if (!value) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  const warnings = typeof showWarningColors === "object" ? false : showWarningColors;
  return /*#__PURE__*/React.createElement(DateLabel, {
    value: value,
    showDistanceInWords: true,
    showWarningColors: warnings
  });
};
export const renderBodyCell = (value, title = "Body") => {
  if (!value) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  if (value.length <= 24) {
    return /*#__PURE__*/React.createElement(Typography, {
      color: "inherit",
      variant: "body1"
    }, value);
  }
  return /*#__PURE__*/React.createElement(HoverDetails, {
    renderItem: () => {
      return /*#__PURE__*/React.createElement(Typography, {
        color: "inherit",
        variant: "body1"
      }, ellipsisText(value, 25));
    },
    title: title,
    value: value
  });
};
export const renderValueCell = value => {
  if (!value) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  return /*#__PURE__*/React.createElement(HoverCopyDetails, {
    value: value
  });
};
export const renderClaimCell = value => {
  if (!value) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  return /*#__PURE__*/React.createElement(HoverDetails, {
    renderItem: () => {
      return /*#__PURE__*/React.createElement(ClaimedIcon, {
        color: "secondary",
        fontSize: "small"
      });
    },
    title: "Claimed By",
    value: value
  });
};
export const renderAttentionCell = (value, alignCenter = true, color = "#ff5d55") => {
  if (!value) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  return /*#__PURE__*/React.createElement("div", {
    style: alignCenter ? iconStyles : {
      display: "flex"
    }
  }, /*#__PURE__*/React.createElement(AttentionRequiredIcon, {
    style: {
      color
    }
  }));
};
export const renderMessageStateCell = value => {
  if (!value) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  return /*#__PURE__*/React.createElement(MessageState, {
    state: value
  });
};
export const renderTransactionStateCell = value => {
  if (!value) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  return /*#__PURE__*/React.createElement(TransactionState, {
    state: value
  });
};
export const renderNotesCell = (value, title, iconType) => {
  if (!value) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  return /*#__PURE__*/React.createElement(MessageNotesDialog, {
    notes: value,
    title: title,
    iconType: iconType
  });
};
export const renderCodeHoverCell = code => {
  if (!code) {
    return /*#__PURE__*/React.createElement(Typography, null);
  }
  const {
    value,
    description
  } = code;
  if (!description) {
    return /*#__PURE__*/React.createElement(Typography, {
      color: "inherit",
      variant: "body1"
    }, value);
  }
  return /*#__PURE__*/React.createElement(HoverDetails, {
    renderItem: () => {
      return /*#__PURE__*/React.createElement(Typography, {
        align: "left",
        color: "inherit",
        variant: "body1"
      }, description);
    },
    title: "Code",
    value: value,
    buttonStyle: {
      width: "unset"
    }
  });
};
export const renderTimeLeft = value => {
  return /*#__PURE__*/React.createElement(TimeLeftCell, {
    value: value
  });
};
export const renderActionOnClickCell = (value, action, disabled = false) => {
  return /*#__PURE__*/React.createElement(TextButton, {
    disabled: disabled,
    onClick: action
  }, value);
};
export const renderIconCell = (value, iconMap) => {
  return value ? iconMap[value] : "n/a";
};
export const renderTypeCell = value => {
  return /*#__PURE__*/React.createElement(Typography, {
    color: "inherit",
    variant: "body1",
    style: {
      fontWeight: 700
    }
  }, value);
};
export const renderMultipleDatesCell = (value, showDateOnly) => {
  const sortValue = value.sort((a, b) => b - a);
  const valueArr = sortValue.length > 5 ? sortValue.slice(0, 5) : sortValue;
  return valueArr === null || valueArr === void 0 ? void 0 : valueArr.map((item, index) => /*#__PURE__*/React.createElement(DateLabel, {
    key: index,
    value: item,
    isMultipleDates: index + 1 !== valueArr.length,
    showDateOnly: showDateOnly
  }));
};